'use strict';

import React, { useState } from 'react';
import classNames from 'classnames';
import PropTypes from 'prop-types';

const RadioButtonGroup = ({
  title,
  name,
  options = [],
  value,
  onChange,
  newPrintsBuilderDeliveryAndFinishIcons,
}) => {
  const [isExpanded, setIsExpanded] = useState(false);

  const displayTitle = title || 'radio-button';
  const inputBaseName = name || displayTitle;

  if (newPrintsBuilderDeliveryAndFinishIcons) {
    const selectedIndex = options.findIndex((opt) => opt && opt.value == value);
    const isSelectedHidden = selectedIndex >= 3;
    const showAll = isExpanded || isSelectedHidden;

    const visibleOptions = showAll ? options : options.slice(0, 3);
    const hasMoreOptions = options.length > 3;

    return (
      <fieldset
        className="horizontal-options-group my-2 w-100"
        aria-label={displayTitle}>
        <legend className="sr-only">{displayTitle}</legend>

        <div
          className="d-flex flex-wrap justify-content-center"
          style={{ gap: '8px' }}>
          {visibleOptions.map((option) => {
            if (!option) return null;

            const id = `${inputBaseName}-${option.label}`
              .replace(/\s/g, '-')
              .toLowerCase();
            const selected = option.value == value;

            return (
              <label
                key={id}
                htmlFor={id}
                className={classNames('horizontal-option-card m-0', {
                  selected: selected,
                  disabled: option.disabled,
                })}
                style={
                  option.disabled
                    ? {
                        opacity: 0.5,
                        pointerEvents: 'none',
                        cursor: 'not-allowed',
                      }
                    : { cursor: 'pointer' }
                }>
                <input
                  id={id}
                  checked={selected}
                  name={`option-group__${inputBaseName}`
                    .replace(/\s/g, '-')
                    .toLowerCase()}
                  type="radio"
                  value={option.value}
                  disabled={option.disabled}
                  onChange={() => onChange(option.value)}
                  className="sr-only"
                />

                {option.iconClass && (
                  <span
                    className={classNames(
                      'horizontal-option-icon',
                      option.iconClass,
                    )}
                    aria-hidden="true"></span>
                )}

                <div className="w-100 text-center">
                  <span className="card-main-label">{option.label}</span>

                  {option.disabled && (
                    <small
                      className="d-block mt-1 font-weight-normal text-muted"
                      style={{ fontSize: '10px', lineHeight: '1.2' }}>
                      Not available at this location
                    </small>
                  )}

                  {!option.disabled &&
                    (option.description || option.subtitle) && (
                      <small
                        className="d-block mt-1 font-weight-normal text-muted"
                        style={{ fontSize: '10px', lineHeight: '1.2' }}>
                        {option.description || option.subtitle}
                      </small>
                    )}
                </div>
              </label>
            );
          })}
        </div>

        {hasMoreOptions && !isSelectedHidden && (
          <button
            type="button"
            className="btn btn-link w-100 mt-2 p-1 text-center font-weight-bold"
            style={{
              fontSize: '12px',
              textDecoration: 'none',
              backgroundColor: '#f4f4f4',
              borderRadius: '4px',
            }}
            onClick={() => setIsExpanded(!isExpanded)}>
            {isExpanded
              ? 'Show Less ▲'
              : `See ${options.length - 3} More Option${options.length - 3 === 1 ? '' : 's'} ▼`}
          </button>
        )}
      </fieldset>
    );
  }

  // -- Old icons render

  return (
    <div
      aria-label={displayTitle}
      className="input__radio-button-group"
      role="radiogroup">
      {title && <h6 className="input__radio-buttons-title">{title}</h6>}
      {options.map((option) => {
        if (!option) return null;

        const id = `${inputBaseName}-${option.label}`
          .replace(/\s/g, '-')
          .toLowerCase();
        let attrDisabled = option.disabled ? { disabled: 'disabled' } : {};

        return (
          <div key={`option_${option.value}`} className="input__radio-button">
            <label
              className={classNames({
                checked: option.value == value,
                option_disabled: option.disabled,
              })}
              htmlFor={id}>
              <input
                aria-checked={option.value == value}
                checked={option.value == value}
                id={id}
                name={`option-group__${inputBaseName}`
                  .replace(/\s/g, '-')
                  .toLowerCase()}
                type="radio"
                value={option.value}
                {...attrDisabled}
                onChange={({ target }) => onChange(target.value)}
              />
              {option.label}
            </label>
          </div>
        );
      })}
    </div>
  );
};

RadioButtonGroup.propTypes = {
  title: PropTypes.string,
  name: PropTypes.string,
  options: PropTypes.array.isRequired,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  onChange: PropTypes.func.isRequired,
  newPrintsBuilderDeliveryAndFinishIcons: PropTypes.bool,
};

export default RadioButtonGroup;
